<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import { formatPrice, planKindLabel, planCtaLabel, parseRef } from '@/Utils/siteAccess'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconBin from '@/components/icon/IconBin.vue'
import SitePlanModal from '@/components/Sites/SitePlanModal.vue'
import SiteStripeConnect from '@/components/Sites/SiteStripeConnect.vue'
import SiteStripeStatus from '@/components/Sites/SiteStripeStatus.vue'
import { useStripeConnect } from '@/composables/useStripeConnect'

// Plans = paywall site products. castr owns create/update/archive (management
// key) and mirrors the content-item mapping to the runtime.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const admin = useSiteAdmin(() => props.siteId)
const route = useRoute()
const router = useRouter()

const loading = ref(true)
const planModal = ref(null)
const pendingArchive = ref(null)
const showArchived = ref(false)
const resuming = ref(false)

// Plans castr paused when Stripe was unlinked are archived on the paywall;
// list them with the active ones so the owner sees what is on hold.
const isPaused = (p) => admin.pausedPlanIds.value.has(p.public_id)
const plans = computed(() => admin.sellablePlans.value.filter((p) => showArchived.value || p.active !== false || isPaused(p)))
const archivedCount = computed(() => admin.sellablePlans.value.filter((p) => p.active === false && !isPaused(p)).length)
const pausedCount = computed(() => admin.sellablePlans.value.filter(isPaused).length)
const paywallUnlinked = computed(() => admin.errors.value.plans?.code === 503 || !props.site.billing?.paywallSiteId)
const canMonetize = computed(() => admin.canMonetize.value)

const refreshMonetization = async () => { await Promise.all([admin.loadMonetization(), admin.loadPlans()]) }

// Unlink lives here too (same DELETE /paywall the Paywall section uses).
const stripeConnect = useStripeConnect({ onUnlinked: refreshMonetization })
const requestUnlink = () => showModal('unlink-stripe-site')
const confirmUnlink = async () => {
	try {
		const res = await stripeConnect.unlink()
		const n = res?.sites?.products ?? 0
		notify({ group: 'success', text: n ? `Stripe unlinked. ${n} paid ${n === 1 ? 'plan' : 'plans'} paused across your sites.` : 'Stripe unlinked.' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not unlink Stripe.' })
	}
}

const resumePlans = async () => {
	resuming.value = true
	try {
		const res = await SiteAdminService.resumeMonetization(props.siteId)
		notify({ group: 'success', text: res.failed?.length ? `Resumed ${res.resumed.length}; ${res.failed.length} could not be resumed.` : `Resumed ${res.resumed.length} paused ${res.resumed.length === 1 ? 'plan' : 'plans'}.` })
		await refreshMonetization()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not resume the paused plans.' })
	}
	resuming.value = false
}

const refLabel = (ref) => {
	const parsed = parseRef(ref)
	if (!parsed) return ref
	return admin.content.value.find((c) => String(c.id) === parsed.id)?.title || `Item #${parsed.id}`
}
// castr strips `category:` refs from older plans and reports them here; the
// plan keeps unlocking them at the paywall until the owner re-maps it.
const legacyCount = (plan) => (plan.legacy_category_refs || []).length
// Trello nk6mbl30: a plan lists items but does not change their access — an
// item that is still Public / Login required is watchable without buying it.
const openItemsOf = (plan) => {
	if (plan.scope !== 'items') return []
	return (plan.items || []).map(parseRef).filter((r) => r?.kind === 'item')
		.map((r) => admin.content.value.find((c) => String(c.id) === r.id))
		.filter((item) => item && (item.access || 'public') !== 'paid')
}
const openItemsTitle = (plan) => {
	const items = openItemsOf(plan)
	const names = items.slice(0, 5).map((item) => `“${item.title}”`).join(', ')
	return `${names}${items.length > 5 ? ` and ${items.length - 5} more` : ''} ${items.length === 1 ? 'is' : 'are'} not Paid — viewers can watch without buying this plan. Change the access under Content › Library, or edit the plan and tick “Set to Paid”.`
}
// Items the plan modal set to Paid on save: refresh them in place.
const onItemsChanged = (items) => {
	for (const item of items) {
		const idx = admin.content.value.findIndex((c) => String(c.id) === String(item.id))
		if (idx >= 0) admin.content.value.splice(idx, 1, { ...admin.content.value[idx], ...item })
	}
}

onMounted(async () => {
	loading.value = true
	await Promise.all([admin.loadPlans(), admin.loadMonetization(), admin.loadContent()])
	loading.value = false
	// `?new=1` (from Promo codes / the Overview checklist) opens the create modal.
	if (route.query.new === '1') {
		const query = { ...route.query }
		delete query.new
		router.replace({ query })
		if (!paywallUnlinked.value) openCreate()
	}
})

const openCreate = () => planModal.value?.show(null, [])
const openEdit = (plan) => planModal.value?.show(plan, [])
const onSaved = (product) => admin.upsertPlan(product)

const requestArchive = (plan) => { pendingArchive.value = plan; showModal('archive-site-plan') }
const confirmArchive = async () => {
	if (!pendingArchive.value) return
	try {
		await SiteAdminService.archivePlan(props.siteId, pendingArchive.value.public_id)
		notify({ group: 'success', text: `"${pendingArchive.value.title}" archived. Existing purchases keep their access.` })
		await admin.loadPlans()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not archive the plan.' })
	}
	pendingArchive.value = null
}
</script>

<template>
	<div>
		<div>
			<div class="mb-4 flex flex-wrap items-center gap-3">
				<div class="min-w-0 flex-1 basis-72">
					<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Plans</h2>
					<p class="mb-0 text-tiny text-surface-7">What viewers can buy: an all-access pass, or plans that unlock the content items you pick. Sold through your Paywall (Stripe) account.</p>
				</div>
				<FormButton size="md" class="shrink-0" :disabled="paywallUnlinked" @click="openCreate">
					<icon-base class="mr-1.5" :width="14" :height="14"><icon-plus /></icon-base>
					New plan
				</FormButton>
			</div>

			<Alert v-if="paywallUnlinked" type="error" class="mb-4">
				This site is not linked to your Paywall account yet{{ admin.errors.value.plans?.message ? ` (${admin.errors.value.plans.message})` : '' }}. Retry publish from the Overview tab; plans become available once the link succeeds.
			</Alert>
			<Alert v-else-if="admin.errors.value.plans" type="error" class="mb-4">{{ admin.errors.value.plans.message }}</Alert>

			<!-- Stripe gate: priced plans need an active Connect account -->
			<SiteStripeConnect v-if="!loading && !paywallUnlinked && !canMonetize" class="mb-4" :stripe="admin.stripe.value || {}" action="sell paid plans" @connected="refreshMonetization" />
			<Alert v-if="!loading && admin.monetizationPaused.value" :type="canMonetize ? 'normal' : 'warning'" class="mb-4">
				<div class="flex flex-wrap items-center gap-3">
					<span class="min-w-48 flex-1">
						<template v-if="canMonetize">Stripe is connected again. {{ pausedCount }} paid {{ pausedCount === 1 ? 'plan is' : 'plans are' }} still paused from the unlink — resume them to start selling.</template>
						<template v-else>Paid plans are paused since Stripe was unlinked{{ admin.monetization.value?.monetization?.at ? ` on ${new Date(admin.monetization.value.monetization.at).toLocaleDateString()}` : '' }}. Paid content stays locked and nothing can be bought until you reconnect.</template>
					</span>
					<FormButton v-if="canMonetize" size="sm" :is-loading="resuming" :disabled="resuming" @click="resumePlans">Resume paused plans</FormButton>
				</div>
			</Alert>

			<Spinner v-if="loading" text="Loading plans…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

			<EmptyState v-else-if="!plans.length && !archivedCount" title="No plans yet" subtitle="Create an all-access pass or a per-item purchase. You can also create plans directly from a content item's access setting.">
				<template v-if="!paywallUnlinked" #actions>
					<FormButton class="w-64 justify-center" @click="openCreate">New plan</FormButton>
				</template>
			</EmptyState>

			<template v-else>
				<ul class="m-0 flex list-none flex-col gap-2 p-0">
					<li v-for="plan in plans" :key="plan.public_id" class="rounded-lg bg-surface-2">
						<div class="flex items-center gap-3 px-3.5 py-3">
							<div class="min-w-0 flex-1">
								<div class="flex flex-wrap items-center gap-2">
									<h3 class="mb-0 truncate text-sm font-medium text-white">{{ plan.title }}</h3>
									<StatusPill pill size="sm" :variant="plan.scope === 'site' ? 'success' : 'muted'">{{ planKindLabel(plan) }}</StatusPill>
									<StatusPill v-if="isPaused(plan)" pill size="sm" variant="warning">Paused — Stripe unlinked</StatusPill>
									<StatusPill v-else-if="plan.active === false" pill size="sm" variant="warning">Archived</StatusPill>
									<StatusPill v-if="legacyCount(plan)" pill size="sm" variant="warning" :title="`Mapped to ${legacyCount(plan)} ${legacyCount(plan) === 1 ? 'category' : 'categories'} before; plans unlock content items only now.`" :data-testid="`plan-remap-${plan.public_id}`">Re-map to items</StatusPill>
									<StatusPill v-if="plan.active !== false && openItemsOf(plan).length" pill size="sm" variant="warning" :title="openItemsTitle(plan)" :data-testid="`plan-open-items-${plan.public_id}`">
										{{ openItemsOf(plan).length }} {{ openItemsOf(plan).length === 1 ? 'item' : 'items' }} still free to watch
									</StatusPill>
								</div>
								<p class="mb-0 truncate text-tiny text-surface-7">
									{{ formatPrice(plan) }}<template v-if="plan.recurring"> · recurring subscription</template><template v-else> · one-time purchase</template>
									· button “{{ planCtaLabel(plan) }}”
									<template v-if="plan.scope === 'items'"> · {{ (plan.items || []).map(refLabel).join(', ') || 'nothing mapped yet' }}</template>
									<template v-else> · everything on the site</template>
								</p>
							</div>
							<div class="flex shrink-0 items-center gap-1">
								<FormButton is-icon type="secondary" size="sm" title="Edit" :disabled="plan.active === false" @click="openEdit(plan)">
									<icon-base :width="16" :height="16" fill="none"><icon-edit /></icon-base>
								</FormButton>
								<FormButton is-icon type="danger" size="sm" title="Archive" :disabled="plan.active === false" @click="requestArchive(plan)">
									<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
								</FormButton>
							</div>
						</div>
					</li>
				</ul>
				<button v-if="archivedCount" type="button" class="mt-3 text-tiny text-surface-8 underline hover:text-white" @click="showArchived = !showArchived">
					{{ showArchived ? 'Hide' : 'Show' }} {{ archivedCount }} archived {{ archivedCount === 1 ? 'plan' : 'plans' }}
				</button>
			</template>

			<!-- Stripe account (same connection the Paywall section manages) -->
			<section v-if="!loading && !paywallUnlinked" class="mt-6 rounded-lg bg-surface-2 p-4 sm:p-5">
				<h2 class="mb-1 text-headline font-semibold text-surface-text-2">Stripe account</h2>
				<p class="mb-4 text-tiny text-surface-7">Payments for this site go to the Stripe Connect account linked to your Castr Paywall.</p>
				<SiteStripeStatus :stripe="admin.stripe.value" :monetization="admin.monetization.value?.monetization" :unlinking="stripeConnect.unlinking.value"
					@connected="refreshMonetization" @unlink="requestUnlink" />
			</section>
		</div>

		<SitePlanModal ref="planModal" :site-id="siteId" :content="admin.content.value"
			:can-monetize="canMonetize" :stripe="admin.stripe.value" :capabilities="admin.planCapabilities.value" @saved="onSaved" @items-changed="onItemsChanged" @stripe-connected="refreshMonetization" />
		<ConfirmModal modal-id="archive-site-plan" modal-type="danger" ok-text="Archive plan" cancel-text="Cancel"
			title="Archive this plan?" :message="`&quot;${pendingArchive?.title || ''}&quot; can no longer be bought. Viewers who already purchased it keep their access.`"
			@modal-confirm="confirmArchive" />
		<ConfirmModal modal-id="unlink-stripe-site" modal-type="danger" ok-text="Unlink Stripe" cancel-text="Cancel"
			title="Unlink your Stripe account?"
			message="Paid plans on all your sites are paused (viewers keep what they already bought) and paid content stays locked until you reconnect. Your Stripe account and its history stay in your Stripe dashboard."
			@modal-confirm="confirmUnlink" />
	</div>
</template>
